import { useState, useEffect, useCallback, useMemo } from 'react';
import { useLanguage } from '../../contexts/LanguageContext';
import api from '../../utils/api';
import './NotificationsPanel.css';

export default function NotificationsPanel({ onUnreadCountChange }) {
  const { t } = useLanguage();
  const [notifications, setNotifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [markingAll, setMarkingAll] = useState(false);
  const [clearingAll, setClearingAll] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);
  const [taskDetail, setTaskDetail] = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  const unreadCount = useMemo(
    () => notifications.filter(n => !n.isRead).length,
    [notifications]
  );

  useEffect(() => {
    if (onUnreadCountChange) onUnreadCountChange(unreadCount);
  }, [unreadCount, onUnreadCountChange]);

  const fetchNotifications = useCallback(async (showSpinner) => {
    if (showSpinner) setLoading(true);
    try {
      const res = await api.get('/notifications?limit=100');
      const data = res.data?.data || res.data?.notifications || [];
      setNotifications(Array.isArray(data) ? data : []);
    } catch {
      // silent
    } finally {
      if (showSpinner) setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchNotifications(true);
    const interval = setInterval(() => fetchNotifications(false), 15000);
    return () => clearInterval(interval);
  }, [fetchNotifications]);

  const markAllAsRead = async () => {
    setMarkingAll(true);
    try {
      await api.post('/notifications/read-all');
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true, readAt: new Date().toISOString() })));
    } catch { /* silent */ }
    setMarkingAll(false);
  };

  const deleteNotification = async (id, e) => {
    if (e) e.stopPropagation();
    if (!id || deletingId) return;
    if (!window.confirm(t('notifications.confirmDelete', 'Delete this notification?'))) return;
    setDeletingId(id);
    try {
      await api.delete(`/notifications/${id}`);
      setNotifications(prev => prev.filter(n => n.id !== id));
      setSelectedNotification(prev => (prev && prev.id === id ? null : prev));
    } catch { /* silent */ }
    setDeletingId(null);
  };

  const clearAll = async () => {
    if (!notifications.length || clearingAll) return;
    if (!window.confirm(t('notifications.confirmClearAll', 'Delete all notifications? This cannot be undone.'))) return;
    setClearingAll(true);
    try {
      await api.delete('/notifications');
      setNotifications([]);
      setSelectedNotification(null);
    } catch { /* silent */ }
    setClearingAll(false);
  };

  const openNotification = async (notif) => {
    // Mark as read
    if (!notif.isRead) {
      try {
        await api.post(`/notifications/${notif.id}/read`);
        setNotifications(prev =>
          prev.map(n => n.id === notif.id ? { ...n, isRead: true, readAt: new Date().toISOString() } : n)
        );
      } catch { /* silent */ }
    }

    setSelectedNotification(notif);
    setTaskDetail(null);

    // Fetch task details if this notification links to a task
    if (notif.targetType === 'task' && notif.targetId) {
      setDetailLoading(true);
      try {
        const res = await api.get(`/tasks/${notif.targetId}`);
        if (res.data?.success && res.data?.data) {
          setTaskDetail(res.data.data);
        }
      } catch { /* silent - task may have been deleted */ }
      setDetailLoading(false);
    }
  };

  const closeModal = () => {
    setSelectedNotification(null);
    setTaskDetail(null);
    setDetailLoading(false);
  };

  const formatWhen = (dateInput) => {
    if (!dateInput) return '';
    const d = new Date(dateInput);
    const now = new Date();
    const diffMs = now - d;
    const diffMin = Math.floor(diffMs / 60000);
    if (diffMin < 1) return t('notifications.justNow', 'Just now');
    if (diffMin < 60) return `${diffMin}m ago`;
    const diffH = Math.floor(diffMin / 60);
    if (diffH < 24) return `${diffH}h ago`;
    const diffD = Math.floor(diffH / 24);
    if (diffD < 7) return `${diffD}d ago`;
    return d.toLocaleDateString('en-GB');
  };

  const formatDate = (dateInput) => {
    if (!dateInput) return '-';
    return new Date(dateInput).toLocaleDateString('en-GB');
  };

  const renderPayload = (item) => {
    const p = item.payload;
    if (!p) return null;
    const rows = [];
    if (p.name) rows.push(['Name', p.name]);
    if (p.email) rows.push(['Email', p.email]);
    if (p.phone) rows.push(['Phone', p.phone]);
    if (p.role) rows.push(['Role', p.role.charAt(0).toUpperCase() + p.role.slice(1)]);
    if (p.address) rows.push(['Address', p.address]);
    if (p.city) rows.push(['City', p.city]);
    if (p.zipCode) rows.push(['Zip Code', p.zipCode]);
    if (p.experience) rows.push(['Experience', `${p.experience} years`]);
    if (rows.length === 0) return null;
    return (
      <div className="np-task-detail">
        <div className="np-detail-grid">
          {rows.map(([label, val], i) => (
            <div key={i} className="np-detail-row">
              <span className="np-detail-label">{label}</span>
              <span className="np-detail-value">{val}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderTaskDetail = (task) => {
    if (!task) return null;

    const rows = [];
    rows.push(['Task ID', `#${task.id}`]);
    if (task.cleaningType) rows.push(['Service Type', task.cleaningType]);
    if (task.title) rows.push(['Service', task.title]);
    if (task.date) rows.push(['Date', formatDate(task.date)]);
    if (task.time) rows.push(['Time', task.endTime ? `${task.time} - ${task.endTime}` : task.time]);
    if (task.hours) rows.push(['Hours', task.hours]);
    if (task.frequency && task.frequency !== 'once') {
      const every = task.recurrenceEvery || 1;
      rows.push(['Frequency', every === 1 ? 'Every week' : `Every ${every} weeks`]);
    } else {
      rows.push(['Frequency', 'One-time']);
    }
    if (task.address) rows.push(['Address', task.address]);
    if (task.status) rows.push(['Status', task.status.charAt(0).toUpperCase() + task.status.slice(1)]);

    // Client info
    if (task.client?.name) rows.push(['Client Name', task.client.name]);
    if (task.client?.phone) rows.push(['Client Phone', task.client.phone]);

    // Cleaner info
    if (task.cleaner?.name) rows.push(['Cleaner Name', task.cleaner.name]);
    if (task.cleaner?.phone) rows.push(['Cleaner Phone', task.cleaner.phone]);

    // Completion info
    if (task.status === 'completed') {
      if (task.actualHours) rows.push(['Actual Hours', task.actualHours]);
      if (task.completedAt) rows.push(['Completed At', formatDate(task.completedAt)]);
      if (task.rating) rows.push(['Rating', `${task.rating}/5`]);
      if (task.ratingComment) rows.push(['Rating Comment', task.ratingComment]);
    }

    // Comments
    if (task.comments) rows.push(['Comments', task.comments]);

    // Checklist
    const checklist = Array.isArray(task.checklist) ? task.checklist.filter(Boolean) : [];

    return (
      <div className="np-task-detail">
        <div className="np-detail-grid">
          {rows.map(([label, val], i) => (
            <div key={i} className="np-detail-row">
              <span className="np-detail-label">{label}</span>
              <span className="np-detail-value">{val}</span>
            </div>
          ))}
        </div>
        {checklist.length > 0 && (
          <div className="np-checklist">
            <span className="np-detail-label">Checklist</span>
            <ul className="np-checklist-list">
              {checklist.map((item, i) => (
                <li key={i}>{item}</li>
              ))}
            </ul>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="np-loading">{t('common.loading', 'Loading...')}</div>;
  }

  return (
    <div className="np-container">
      <div className="np-header">
        <h2>{t('dashboard.notifications', 'Notifications')}</h2>
        <div className="np-header-actions">
          {unreadCount > 0 && (
            <button className="np-mark-all-btn" onClick={markAllAsRead} disabled={markingAll}>
              {markingAll ? t('dashboard.marking', 'Marking...') : t('dashboard.markAllRead', 'Mark all read')}
            </button>
          )}
          {notifications.length > 0 && (
            <button className="np-clear-all-btn" onClick={clearAll} disabled={clearingAll}>
              {clearingAll ? t('dashboard.clearing', 'Clearing...') : t('dashboard.clearAll', 'Clear all')}
            </button>
          )}
        </div>
      </div>

      {notifications.length === 0 ? (
        <div className="np-empty">
          <div className="np-empty-icon">🔔</div>
          <p>{t('dashboard.noNotifications', 'No notifications yet')}</p>
          <span>{t('dashboard.noNotificationsText', 'New updates will appear here.')}</span>
        </div>
      ) : (
        <div className="np-list">
          {notifications.map(notif => (
            <div
              key={notif.id}
              className={`np-card ${!notif.isRead ? 'unread' : ''}`}
              onClick={() => openNotification(notif)}
            >
              <div className="np-card-header">
                <span className="np-card-title">{notif.title}</span>
                <div className="np-card-meta">
                  {!notif.isRead && <span className="np-unread-dot" />}
                  <span className="np-card-time">{formatWhen(notif.createdAt)}</span>
                  <button
                    type="button"
                    className="np-delete-btn"
                    title={t('common.delete', 'Delete')}
                    aria-label={t('common.delete', 'Delete')}
                    disabled={deletingId === notif.id}
                    onClick={(e) => deleteNotification(notif.id, e)}
                  >
                    {deletingId === notif.id ? '…' : '🗑'}
                  </button>
                </div>
              </div>
              <p className="np-card-message">{notif.message}</p>
            </div>
          ))}
        </div>
      )}

      {selectedNotification && (
        <div className="np-modal-overlay" onClick={closeModal}>
          <div className="np-modal" onClick={e => e.stopPropagation()}>
            <div className="np-modal-header">
              <h3>{selectedNotification.title}</h3>
              <button className="np-modal-close" onClick={closeModal}>×</button>
            </div>
            <div className="np-modal-body">
              <p className="np-modal-time">{formatWhen(selectedNotification.createdAt)}</p>
              <p className="np-modal-message">{selectedNotification.message}</p>

              {/* Task detail */}
              {detailLoading && (
                <div className="np-detail-loading">{t('common.loading', 'Loading...')}</div>
              )}
              {taskDetail && renderTaskDetail(taskDetail)}

              {/* User/job_seeker payload */}
              {!taskDetail && !detailLoading && renderPayload(selectedNotification)}

              <div className="np-modal-actions">
                <button
                  className="np-modal-delete-btn"
                  onClick={(e) => deleteNotification(selectedNotification.id, e)}
                  disabled={deletingId === selectedNotification.id}
                >
                  {deletingId === selectedNotification.id
                    ? t('dashboard.deleting', 'Deleting...')
                    : t('common.delete', 'Delete')}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
